import React, { useState, useRef, useEffect } from "react";
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  StyleSheet,
  Modal,
  ScrollView,
} from "react-native";

import { observer, Observer } from "mobx-react";
import { Ionicons } from "@expo/vector-icons";

import Fonts from "../../Resources/Fonts";
import Colors from "../../Resources/Color";
import GlobalStyles from "../../Resources/Styles";
import Block from "../Firestore/Block";
import Workout from "../Firestore/Workout";
import { useDevice } from "../_layout";
import { v4 as uuidv4 } from "uuid";
import { useTranslation } from "react-i18next";
import { ProtocolTitles } from "../../Resources/Constants";
import {
  ADD_BLOCK_MIN_WEEKS,
  decrementWeeksValue,
  incrementWeeksValue,
  isWeeksValueValid,
  normalizeFolderLabel,
  parseWeeksValue,
  resolveWeeksDraftOnBlur,
  resolveWeeksForSave,
  sanitizeWeeksInputDraft,
} from "./addBlockViewUtils";

const AddBlockView = observer(
  ({ isVisible, onClose, block, client, coach, preselectedFolderLabel = "" }) => {
    const { t, i18n } = useTranslation();
    const { isMobile } = useDevice();

    const predefinedText =
      block && block.name ? block.name : t("newBlock-string");
    const titleKey = block ? "editBlock-string" : "createBlock-string";
    const modalTitle = t(titleKey).toUpperCase();

    const [blockName, setBlockName] = useState(block?.name ?? "");
    const [weeksDraft, setWeeksDraft] = useState(
      String(resolveWeeksForSave(block?.totalWeeks)),
    );
    const [folderName, setFolderName] = useState(
      normalizeFolderLabel(block?.label ?? preselectedFolderLabel),
    );

    const [isNameHovered, setIsNameHovered] = useState(false);
    const [isWeeksHovered, setIsWeeksHovered] = useState(false);
    const [isFolderHovered, setIsFolderHovered] = useState(false);

    const blockNameInputRef = useRef(null);
    const minEditableWeeks = block
      ? Math.max(ADD_BLOCK_MIN_WEEKS, Number(block?.getWeeks) || 0)
      : ADD_BLOCK_MIN_WEEKS;
    const parsedWeeksDraft = parseWeeksValue(weeksDraft);
    const isEditWeeksValid = !block
      ? true
      : isWeeksValueValid(weeksDraft, { min: minEditableWeeks });
    const stepperWeeksOptions = block
      ? {
          min: minEditableWeeks,
          fallback: parseWeeksValue(block?.totalWeeks) ?? minEditableWeeks,
        }
      : undefined;

    useEffect(() => {
      if (!isVisible) return;

      setBlockName(block?.name ?? "");
      setWeeksDraft(String(resolveWeeksForSave(block?.totalWeeks)));
      setFolderName(normalizeFolderLabel(block?.label ?? preselectedFolderLabel));

      const timeoutId = setTimeout(() => {
        blockNameInputRef.current?.focus?.();
      }, 100);

      return () => clearTimeout(timeoutId);
    }, [block, isVisible, preselectedFolderLabel]);

    const cancel = () => {
      onClose();
    };

    const handleSave = () => {
      const newName = blockName === "" ? predefinedText : blockName;
      const totalWeeks = block
        ? parsedWeeksDraft
        : resolveWeeksForSave(weeksDraft);
      const savedFolderName = normalizeFolderLabel(folderName);

      if (block && !isEditWeeksValid) {
        return;
      }

      if (!block) {
        if (client) {
          const newBlock = new Block(
            uuidv4(),
            newName,
            "",
            [],
            false,
            false,
            client,
            1,
            totalWeeks,
            savedFolderName,
          );
          const newWorkout = new Workout(
            uuidv4(),
            null,
            i18n.language === "es" ? "Día 1" : "Day 1",
            0,
            1,
            false,
            newBlock,
            [],
          );
          newBlock.workouts.push(newWorkout);
          newBlock.toggleProtocolSeriesShown(
            ProtocolTitles.PRIMARY_BENCH_PRESS,
          );
          newBlock.toggleProtocolSeriesShown(ProtocolTitles.PRIMARY_DEADLIFT);
          newBlock.toggleProtocolSeriesShown(ProtocolTitles.PRIMARY_SQUAT);

          client.blocks.push(newBlock);
          client.updateClientTransactionData(newBlock.id);
          client.startOrResetBatchTimer();
        } else if (coach) {
          const nextOrder = Array.isArray(coach.blockLibrary)
            ? coach.blockLibrary.reduce((maxOrder, entry) => {
                const parsed = Number(entry?.order);
                if (!Number.isFinite(parsed)) return maxOrder;
                return parsed > maxOrder ? parsed : maxOrder;
              }, -1) + 1
            : 0;

          const newBlock = new Block(
            uuidv4(),
            newName,
            "",
            [],
            false,
            false,
            null,
            1,
            totalWeeks,
            savedFolderName,
            undefined,
            coach,
            nextOrder,
          );
          const newWorkout = new Workout(
            uuidv4(),
            null,
            i18n.language === "es" ? "Día 1" : "Day 1",
            0,
            1,
            false,
            newBlock,
            [],
          );
          newBlock.toggleProtocolSeriesShown(
            ProtocolTitles.PRIMARY_BENCH_PRESS,
          );
          newBlock.toggleProtocolSeriesShown(ProtocolTitles.PRIMARY_DEADLIFT);
          newBlock.toggleProtocolSeriesShown(ProtocolTitles.PRIMARY_SQUAT);
          newBlock.workouts.push(newWorkout);

          coach.blockLibrary.push(newBlock);
          coach.updateCoachTransactionBlocks(newBlock.id);
          coach.startOrResetBatchTimer();
        }
      } else {
        block.setProperty("name", newName);
        block.setProperty("totalWeeks", totalWeeks);
        block.setProperty("label", savedFolderName);
      }

      onClose();
    };

    const handleWeeksInputChange = (text) => {
      setWeeksDraft(sanitizeWeeksInputDraft(text));
    };

    const handleWeeksInputBlur = () => {
      if (block) return;
      setWeeksDraft(resolveWeeksDraftOnBlur(weeksDraft));
    };

    const handleFieldKeyPress = (e) => {
      const key = e?.nativeEvent?.key ?? e?.key;
      if (key === "Enter" || e?.keyCode === 13) {
        if (block && !isEditWeeksValid) {
          return;
        }
        handleSave();
      }
    };

    const ActionButton = ({ isSave }) => {
      const [isHovered, setIsHovered] = useState(false);
      const labelKey = isSave ? "save-string" : "cancel-string";
      const isDisabled = isSave && block && !isEditWeeksValid;

      if (isSave) {
        return (
          <TouchableOpacity
            onPress={isDisabled ? undefined : handleSave}
            onMouseEnter={() => !isMobile && setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            activeOpacity={isDisabled ? 1 : 0.7}
          >
            <View
              style={[
                styles.actionButton,
                styles.saveButton,
                isHovered && !isDisabled && styles.saveButtonHovered,
                isDisabled && styles.saveButtonDisabled,
              ]}
            >
              <Text style={styles.saveButtonText}>{t(labelKey)}</Text>
            </View>
          </TouchableOpacity>
        );
      }

      return (
        <TouchableOpacity
          onPress={cancel}
          onMouseEnter={() => !isMobile && setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          <View
            style={[
              styles.actionButton,
              styles.cancelButton,
              isHovered && styles.cancelButtonHovered,
            ]}
          >
            <Text
              style={[
                styles.cancelButtonText,
                isHovered && styles.cancelButtonTextHovered,
              ]}
            >
              {t(labelKey)}
            </Text>
          </View>
        </TouchableOpacity>
      );
    };

    const FolderSuggestionButton = ({ label }) => {
      const isSelected =
        label.toLowerCase() === normalizeFolderLabel(folderName).toLowerCase();
      const [isHovered, setIsHovered] = useState(false);
      const isActiveHover = isHovered && !isSelected;
      const folderIconColor = isSelected
        ? Colors.whiteColor
        : isActiveHover
          ? Colors.blackColor
          : Colors.darkgrayColor;

      return (
        <TouchableOpacity
          onPress={() => setFolderName(label)}
          onMouseEnter={() => !isMobile && setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          style={{ maxWidth: "100%" }}
        >
          <View
            style={[
              styles.folderSuggestionChip,
              isSelected && styles.folderSuggestionChipSelected,
              isActiveHover && styles.folderSuggestionChipHovered,
            ]}
          >
            <View style={styles.folderSuggestionContentRow}>
              <Ionicons
                name="folder-outline"
                size={12}
                color={folderIconColor}
                style={styles.folderSuggestionIcon}
              />
              <Text
                numberOfLines={1}
                style={[
                  styles.folderSuggestionText,
                  isSelected && styles.folderSuggestionTextSelected,
                  isActiveHover && styles.folderSuggestionTextHovered,
                ]}
              >
                {label}
              </Text>
            </View>
          </View>
        </TouchableOpacity>
      );
    };

    return (
      <Modal
        animationType="fade"
        transparent={true}
        visible={isVisible}
        onRequestClose={onClose}
      >
        <View style={GlobalStyles.centeredView}>
          <TouchableOpacity
            onPress={onClose}
            style={GlobalStyles.touchableBackground}
          >
            <View />
          </TouchableOpacity>
          <View
            style={{
              position: "absolute",
              maxHeight: "90%",
              maxWidth: "90%",
            }}
            onStartShouldSetResponder={() => false}
          >
            <View style={[styles.modalView, { width: 420, flexShrink: 1 }]}>
              <ScrollView
                showsVerticalScrollIndicator={false}
                style={{ width: "100%", maxHeight: "100%" }}
                contentContainerStyle={{
                  alignItems: "center",
                  justifyContent: "center",
                  flexGrow: 1,
                }}
              >
                <View style={{ alignItems: "center", width: "100%" }}>
                  <Text style={Fonts.bold18Centered}>{modalTitle}</Text>

                  <TextInput
                    ref={blockNameInputRef}
                    style={[
                      GlobalStyles.textInputStyle,
                      {
                        borderRadius: 12,
                        borderColor: isNameHovered
                          ? "rgba(0, 0, 0, 0.45)"
                          : Colors.borderColor,
                        fontSize: isMobile ? 16 : undefined,
                      },
                    ]}
                    placeholder={predefinedText}
                    placeholderTextColor={Colors.grayColor}
                    value={blockName}
                    onChangeText={setBlockName}
                    maxLength={40}
                    onMouseEnter={() => !isMobile && setIsNameHovered(true)}
                    onMouseLeave={() => setIsNameHovered(false)}
                    onKeyPress={handleFieldKeyPress}
                  />

                  <View style={styles.weeksRow}>
                    <Text style={Fonts.medium14}>{t("weeksDots-string")}</Text>
                    <View
                      style={[
                        styles.weeksControl,
                        isWeeksHovered && styles.weeksControlHovered,
                      ]}
                      onMouseEnter={() => !isMobile && setIsWeeksHovered(true)}
                      onMouseLeave={() => setIsWeeksHovered(false)}
                    >
                      <TouchableOpacity
                        style={styles.weekArrowButton}
                        onPress={() =>
                          setWeeksDraft((prev) =>
                            String(decrementWeeksValue(prev, stepperWeeksOptions)),
                          )
                        }
                      >
                        <Ionicons
                          name="remove"
                          size={16}
                          color={Colors.darkgrayColor}
                        />
                      </TouchableOpacity>

                      <TextInput
                        style={[
                          styles.weekInput,
                          {
                            color:
                              block && !isEditWeeksValid
                                ? Colors.redColor
                                : Colors.blackColor,
                            fontSize: isMobile ? 16 : undefined,
                            borderColor: isWeeksHovered
                              ? "rgba(0, 0, 0, 0.45)"
                              : Colors.borderColor,
                          },
                        ]}
                        value={weeksDraft}
                        onChangeText={handleWeeksInputChange}
                        onBlur={handleWeeksInputBlur}
                        onKeyPress={handleFieldKeyPress}
                        placeholder={String(resolveWeeksForSave(undefined))}
                        placeholderTextColor={Colors.grayColor}
                        inputMode="numeric"
                        keyboardType="numeric"
                        maxLength={2}
                      />

                      <TouchableOpacity
                        style={styles.weekArrowButton}
                        onPress={() =>
                          setWeeksDraft((prev) =>
                            String(incrementWeeksValue(prev, stepperWeeksOptions)),
                          )
                        }
                      >
                        <Ionicons
                          name="add"
                          size={16}
                          color={Colors.darkgrayColor}
                        />
                      </TouchableOpacity>
                    </View>
                  </View>

                  {client && (
                    <View style={{ width: "100%", marginTop: 14 }}>
                      <View style={styles.folderHeaderRow}>
                        <Text style={Fonts.medium14}>{t("label-string")}</Text>
                        <Text style={styles.optionalText}>
                          {t("optional-string")}
                        </Text>
                      </View>

                      <View
                        style={[
                          styles.folderContainer,
                          isFolderHovered && styles.folderContainerHovered,
                        ]}
                        onMouseEnter={() => !isMobile && setIsFolderHovered(true)}
                        onMouseLeave={() => setIsFolderHovered(false)}
                      >
                        <TextInput
                          style={[
                            styles.folderInput,
                            { fontSize: isMobile ? 16 : undefined },
                          ]}
                          placeholder={t("macrocyclePlaceholder-string")}
                          placeholderTextColor={Colors.grayColor}
                          value={folderName}
                          onChangeText={setFolderName}
                          maxLength={20}
                          onKeyPress={handleFieldKeyPress}
                        />

                        <Observer>
                          {() => {
                            const filteredLabels =
                              client.getFilteredLabels(folderName);
                            const hasFolderSuggestions = filteredLabels.length > 0;
                            const hasTypedFolder =
                              normalizeFolderLabel(folderName) !== "";

                            if (!hasFolderSuggestions && !hasTypedFolder) {
                              return null;
                            }

                            return (
                              <>
                                <View style={styles.folderDivider} />
                                {hasFolderSuggestions ? (
                                  <ScrollView
                                    style={styles.folderSuggestionsScroll}
                                    contentContainerStyle={styles.folderSuggestionsWrap}
                                    showsVerticalScrollIndicator={true}
                                  >
                                    {filteredLabels.map((label, index) => (
                                      <FolderSuggestionButton
                                        label={label}
                                        key={`${label}-${index}`}
                                      />
                                    ))}
                                  </ScrollView>
                                ) : (
                                  <Text style={styles.folderEmptyText}>
                                    {t("labelEmptyText-string")}
                                  </Text>
                                )}
                              </>
                            );
                          }}
                        </Observer>
                      </View>
                    </View>
                  )}
                </View>

                <View style={styles.actionsRow}>
                  <ActionButton isSave={false} />
                  <ActionButton isSave={true} />
                </View>
              </ScrollView>
            </View>
          </View>
        </View>
      </Modal>
    );
  },
);

const styles = StyleSheet.create({
  modalView: {
    backgroundColor: Colors.whiteColor,
    borderRadius: 18,
    paddingHorizontal: 24,
    paddingVertical: 24,
    alignItems: "center",
    justifyContent: "space-between",
    maxHeight: "100%",
    maxWidth: "100%",
    borderWidth: 1,
    borderColor: Colors.borderColor,
  },
  weeksRow: {
    alignItems: "center",
    justifyContent: "space-between",
    marginTop: 10,
    width: "100%",
    flexDirection: "row",
  },
  weeksControl: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 0,
    gap: 6,
    height: 40,
  },
  weeksControlHovered: {},
  weekArrowButton: {
    width: 26,
    height: 26,
    alignItems: "center",
    justifyContent: "center",
    borderRadius: 8,
    backgroundColor: "transparent",
  },
  weekInput: {
    width: 48,
    minWidth: 48,
    height: 40,
    borderRadius: 12,
    borderWidth: 0.1,
    borderColor: Colors.borderColor,
    backgroundColor: Colors.whiteColor,
    textAlign: "center",
    fontSize: 14,
    fontWeight: "400",
    color: Colors.blackColor,
    paddingHorizontal: 0,
    outlineStyle: "none",
  },
  folderHeaderRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 8,
  },
  optionalText: {
    fontSize: 12,
    color: Colors.grayColor,
  },
  folderContainer: {
    width: "100%",
    borderColor: Colors.borderColor,
    backgroundColor: Colors.whiteColor,
    borderWidth: 0.1,
    borderRadius: 12,
    overflow: "hidden",
  },
  folderContainerHovered: {
    borderColor: "rgba(0, 0, 0, 0.45)",
  },
  folderInput: {
    height: 40,
    color: Colors.blackColor,
    paddingHorizontal: 12,
    outlineStyle: "none",
  },
  folderDivider: {
    height: 1,
    backgroundColor: "rgba(0, 0, 0, 0.09)",
    width: "100%",
  },
  folderSuggestionsScroll: {
    maxHeight: 140,
  },
  folderSuggestionsWrap: {
    flexDirection: "row",
    flexWrap: "wrap",
    alignItems: "center",
    gap: 8,
    padding: 10,
  },
  folderSuggestionChip: {
    borderRadius: 8,
    borderWidth: 1,
    borderColor: Colors.borderColor,
    backgroundColor: Colors.whiteColor,
    maxWidth: 170,
    paddingVertical: 4,
    paddingHorizontal: 8,
  },
  folderSuggestionChipHovered: {
    borderColor: "rgba(0, 0, 0, 0.4)",
    backgroundColor: Colors.hoveredGray,
  },
  folderSuggestionChipSelected: {
    borderColor: Colors.bubbleColor,
    backgroundColor: Colors.bubbleColor,
  },
  folderSuggestionContentRow: {
    flexDirection: "row",
    alignItems: "center",
  },
  folderSuggestionIcon: {
    marginRight: 4,
  },
  folderSuggestionText: {
    fontSize: 12,
    color: Colors.darkgrayColor,
    fontWeight: "500",
    flexShrink: 1,
  },
  folderSuggestionTextHovered: {
    color: Colors.blackColor,
  },
  folderSuggestionTextSelected: {
    color: Colors.whiteColor,
  },
  folderEmptyText: {
    alignSelf: "center",
    textAlign: "center",
    color: "rgba(0, 0, 0, 0.4)",
    fontSize: 12,
    paddingVertical: 10,
    paddingHorizontal: 20,
  },
  actionsRow: {
    flexDirection: "row",
    justifyContent: "space-around",
    width: "100%",
    marginTop: 18,
  },
  actionButton: {
    borderRadius: 14,
    borderWidth: 1,
    width: 88,
    height: 36,
    alignItems: "center",
    justifyContent: "center",
  },
  saveButton: {
    backgroundColor: Colors.bubbleColor,
    borderColor: Colors.bubbleColor,
  },
  saveButtonHovered: {
    opacity: 0.85,
  },
  saveButtonDisabled: {
    opacity: 0.5,
  },
  saveButtonText: {
    color: Colors.whiteColor,
    fontSize: 13,
  },
  cancelButton: {
    backgroundColor: Colors.whiteColor,
    borderColor: Colors.redColor,
  },
  cancelButtonHovered: {
    backgroundColor: Colors.redColor,
  },
  cancelButtonText: {
    color: Colors.redColor,
    fontSize: 13,
  },
  cancelButtonTextHovered: {
    color: Colors.whiteColor,
  },
});

export default AddBlockView;
